import React from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  Divider,
  IconButton,
} from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import LanguageIcon from "@mui/icons-material/Language";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

// Helper function to format ISO Dates to clean Readable String (YYYY-MM-DD)
const formatDate = (dateString) => {
  if (!dateString) return "Present";
  const date = new Date(dateString);
  return date.toISOString().split("T")[0];
};

// =======================================================
// EXPIRIENCE CARDS LIST COMPONENT
// =======================================================
const WorkExperienceList = ({ workExperiences, onDelete }) => {
  if (!workExperiences || workExperiences.length === 0) {
    return null; // Agar koi data nahi hoga toh default layout intact rahega
  }

  const handleDeleteClick = (id) => {
    if (window.confirm("Do you want to delete this experience record?")) {
      if (onDelete) onDelete(id);
    }
  };

  // Helper mapping helper to display professional dynamic colors for ENUMS
  const getTypeColor = (type) => {
    switch (type) {
      case "full_time": return "primary";
      case "part_time": return "secondary";
      case "contract": return "warning";
      case "internship": return "info";
      default: return "default";
    }
  };

  return (
    <Box sx={{ mt: 5 }}>
      {/* SECTION HEADER */}
      <Typography variant="subtitle1" fontWeight="700" color="#1e293b" mb={2}>
        Employment History ({workExperiences.length})
      </Typography>

      {/* RESPONSIVE CARDS GRID SYSTEM */}
      <Grid container spacing={2}>
        {workExperiences.map((exp) => (
          <Grid item xs={12} sm={6} key={exp.id}>
            <Card
              sx={{
                borderRadius: "16px",
                border: "1px solid #e2e8f0",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.03)",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 20px rgba(0, 0, 0, 0.06)",
                },
              }}
            >
              <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
                
                {/* HEADER SECTION: ROLE & TRASH */}
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                  <Box display="flex" alignItems="center" gap={1} sx={{ maxWidth: "70%" }}>
                    <BusinessIcon color="action" sx={{ fontSize: 22, color: "#4f46e5", flexShrink: 0 }} />
                    <Typography 
                      variant="h6" 
                      fontWeight="700" 
                      color="#1e293b" 
                      sx={{ fontSize: "16px", textTransform: "uppercase", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                    >
                      {exp.designation}
                    </Typography>
                  </Box>

                  {/* ACTION PANEL CONTROL */}
                  <Box display="flex" alignItems="center" gap={1}>
                    <Chip
                      label={exp.employee_type ? exp.employee_type.replace("_", " ").toUpperCase() : "FULL TIME"}
                      size="small"
                      color={getTypeColor(exp.employee_type)}
                      sx={{ fontWeight: "700", fontSize: "10px", borderRadius: "6px" }}
                    />
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteClick(exp.id)}
                      sx={{
                        bgcolor: "rgba(239, 68, 68, 0.05)",
                        p: 0.6,
                        "&:hover": { bgcolor: "rgba(239, 68, 68, 0.15)" }
                      }}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                </Box>

                {/* COMPANY TITLE */}
                <Typography variant="body2" fontWeight="600" color="#475569" mb={2}>
                  {exp.company_name}
                </Typography>

                <Divider sx={{ borderStyle: "dashed", mb: 2 }} />

                {/* TIMELINE METRICS ROW */}
                <Grid container spacing={1} sx={{ mb: 2.5 }}>
                  <Grid item xs={7} display="flex" alignItems="center" gap={0.5} color="#64748b">
                    <CalendarTodayIcon sx={{ fontSize: 15 }} />
                    <Typography variant="caption" fontWeight="600">
                      {formatDate(exp.joining_date)} to {exp.currently_working ? "Present" : formatDate(exp.ending_date)}
                    </Typography>
                  </Grid>

                  <Grid item xs={5} display="flex" alignItems="center" gap={0.5} color="#64748b" justifyContent="flex-end">
                    <HourglassEmptyIcon sx={{ fontSize: 15 }} />
                    <Typography variant="caption" fontWeight="600" sx={{ textTransform: "uppercase" }}>
                      Dur: {exp.experience || "N/A"}
                    </Typography>
                  </Grid>
                </Grid>

                {/* WEBSITE URL TRIGGER SUBMIT BUTTON */}
                {exp.company_url ? (
                  <Button
                    fullWidth
                    variant="outlined"
                    size="small"
                    startIcon={<LanguageIcon sx={{ fontSize: 14 }} />}
                    onClick={() => window.open(exp.company_url, "_blank", "noopener,noreferrer")}
                    sx={{
                      textTransform: "none",
                      fontWeight: "600",
                      borderRadius: "8px",
                      bgcolor: "#f8fafc",
                      borderColor: "#cbd5e1",
                      color: "#475569",
                      fontSize: "12px",
                      py: 0.8,
                      "&:hover": {
                        bgcolor: "#f1f5f9",
                        borderColor: "#94a3b8",
                        color: "#1e293b",
                      },
                    }}
                  >
                    Visit Company Portal
                  </Button>
                ) : (
                  <Button fullWidth disabled size="small" sx={{ borderRadius: "8px", fontSize: "12px" }}>
                    No Portal Website Available
                  </Button>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default WorkExperienceList;